/**
 * The search box and the pager.
 *
 * One pair of components for every list in the app, so a customer who learns how one table
 * behaves has learned all of them — and so a change to how searching feels is made once.
 */

import type { ListParams, PageMeta } from "@/lib/list-params";
import { IconChevronDown, IconClose, IconSearch } from "./icons";

/**
 * A search box with no button.
 *
 * There is nothing to submit: the list follows what is typed, a quarter of a second behind. The
 * spinner appears only while the box and the results disagree, so a fast search never flashes one
 * and a slow one never looks stuck.
 */
export function SearchBox({
  params,
  placeholder,
  className = "",
}: {
  params: ListParams;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={`relative max-w-sm flex-1 ${className}`}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
        <IconSearch size={16} />
      </span>
      <input
        type="search"
        value={params.text}
        onChange={(event) => params.setText(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="field pl-9 pr-9"
        /* The browser's own clear button would bypass our state, so it is suppressed in CSS and
           replaced below with one that goes through `clear()`. */
      />
      {params.pending ? (
        <span className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin rounded-full border-2 border-ink-200 border-t-brand-600" />
      ) : params.text ? (
        <button
          type="button"
          onClick={params.clear}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
        >
          <IconClose size={15} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Which page numbers to offer.
 *
 * Always the first and the last, always the current and its neighbours, and an ellipsis for
 * whatever is skipped. A pager that lists forty numbered pages is not navigation, it is a wall.
 *
 * `most` is how many numbers there is room for. A phone gets fewer: seven numbers and two arrows
 * do not fit across 390px, and a pager that pushes the page sideways is worse than one that
 * offers fewer stops.
 */
function pageNumbers(current: number, pages: number, most = 7): Array<number | "gap"> {
  if (pages <= most) return Array.from({ length: pages }, (_, i) => i + 1);
  const reach = most <= 5 ? 0 : 1;
  const around = [current - reach, current, current + reach].filter((n) => n > 1 && n < pages);
  const shown = [1, ...around, pages];
  const out: Array<number | "gap"> = [];
  let previous = 0;
  for (const n of shown) {
    if (n - previous > 1) out.push("gap");
    out.push(n);
    previous = n;
  }
  return out;
}

export function Pagination({
  meta,
  params,
  noun,
}: {
  meta: PageMeta | undefined;
  params: ListParams;
  /** What is being counted, plural — "invoices", "clients". */
  noun: string;
}) {
  if (!meta || meta.total === 0) return null;
  // One page of results needs a count, not a pager. Hiding the count too would leave the user
  // guessing whether they are seeing everything.
  const first = (meta.page - 1) * meta.perPage + 1;
  const last = Math.min(meta.total, first + meta.perPage - 1);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-200 px-4 py-3">
      <p className="text-sm text-ink-500">
        <span className="tabular font-medium text-ink-700">
          {first}–{last}
        </span>{" "}
        of <span className="tabular font-medium text-ink-700">{meta.total}</span> {noun}
      </p>

      {meta.pages > 1 ? (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn-sm btn-secondary"
            disabled={meta.page <= 1}
            onClick={() => params.setPage(meta.page - 1)}
            aria-label="Previous page"
          >
            <IconChevronDown size={15} className="rotate-90" />
          </button>

          {/* The same pager twice, at two widths. Which numbers fit is a question about the screen,
              and the screen answers it here rather than in a resize listener. */}
          {[
            { most: 4, on: "flex sm:hidden" },
            { most: 7, on: "hidden sm:flex" },
          ].map(({ most, on }) => (
            <span key={most} className={`${on} items-center gap-1`}>
              {pageNumbers(meta.page, meta.pages, most).map((entry, index) =>
                entry === "gap" ? (
                  <span key={`gap-${index}`} className="px-1 text-sm text-ink-400" aria-hidden="true">
                    …
                  </span>
                ) : (
                  <button
                    key={entry}
                    type="button"
                    aria-current={entry === meta.page ? "page" : undefined}
                    onClick={() => params.setPage(entry)}
                    className={`tabular min-w-9 rounded-[var(--radius-control)] px-2.5 py-2 text-sm font-medium ${
                      entry === meta.page
                        ? "bg-brand-600 text-white"
                        : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                    }`}
                  >
                    {entry}
                  </button>
                ),
              )}
            </span>
          ))}

          <button
            type="button"
            className="btn-sm btn-secondary"
            disabled={meta.page >= meta.pages}
            onClick={() => params.setPage(meta.page + 1)}
            aria-label="Next page"
          >
            <IconChevronDown size={15} className="-rotate-90" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
